import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import {
  CategoryInterface,
  MenuInterface,
  Modifier,
  ModifierGroupInterface,
  ModifierId,
  ProductInterface,
} from '../services/exports/Interfaces';
import collect, { Collection } from 'collect.js';
import useInitialData from '../hooks/global/useInitialData';
import MenuResource from '../services/resources/MenuResource';
import useImagePreloader from '../hooks/utility/useImagePreloader';
import { CategoryType, EntryType, ModifierGroupType, ORDER_METHODS } from '../services/exports/Constants';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../store/types';
import useRewardAccount from '../hooks/loyalty/useRewardAccount';
import { Integer } from 'schema-dts';
import toFlatArray from '../utils/to-flat.util';
import { CompleteBasketItem } from './BasketProvider';
import useLoyaltyProgram from '../hooks/loyalty/useLoyaltyProgram';

interface Props {
  children: ReactElement;
}

export interface MenuContextInterface {
  menus: Collection<MenuInterface>;
  selectedMenu: MenuInterface;
  setSelectedMenu: (value: MenuInterface) => void;
  categories: CategoryInterface[];
  compileProduct: (id: number) => ProductInterface;
  findFreeProduct: (id: number) => ProductInterface;
  applyOrderMethodPrices: <T extends ProductInterface | Modifier>(item: T) => T;
  isBogoProduct: (searchProductId: Integer) => boolean;
  getUpsellModifierGroup: (
    basketItems: CompleteBasketItem[],
    openedProduct: ProductInterface,
  ) => ModifierGroupInterface;
}

export const MenuContext = React.createContext<MenuContextInterface>(null);

export default function MenuProvider(props: Props) {
  const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Providers:MenuProvider' });

  const { cached_order } = useSelector((state: StoreInterface) => state.order);

  const { children } = props;
  const { data } = useInitialData();
  const { company } = data;

  const { preloadImages } = useImagePreloader();
  const { hasFreeItem } = useRewardAccount();
  const { isActive: isLoyaltyProgramActive } = useLoyaltyProgram();

  const [selectedMenu, setSelectedMenu] = useState(null);

  const applyOrderMethodPrices = useCallback(
    <T extends ProductInterface | Modifier>(item: T): T => {
      return {
        ...item,
        base_price: item?.original_price, // a price that isn't affected by any discounts
        original_price:
          item?.original_order_method_prices?.[cached_order?.method ?? ORDER_METHODS.PICKUP] ?? item?.original_price,
        actual_price:
          item?.actual_order_method_prices?.[cached_order?.method ?? ORDER_METHODS.PICKUP] ?? item?.actual_price,
      };
    },
    [cached_order?.method],
  );
  const modifierGroupsState = useMemo(() => collect(company?.modifier_groups), [company?.modifier_groups]);

  const modifierGroups = useMemo(() => {
    return modifierGroupsState.map((item) => {
      const itemModifiers = item.modifier_ids?.map((modifier) => setupModifier(modifier)) ?? [];

      return {
        ...item,
        modifiers: itemModifiers,
      };
    });
  }, [modifierGroupsState, company?.modifiers, company?.products]);

  function setupModifier(modifier: ModifierId) {
    const entry =
      modifier.type === EntryType.Product
        ? { ...company?.products[modifier.id] }
        : { ...company?.modifiers[modifier.id] };

    if (entry?.modifier_group_ids?.length && !entry.modifier_groups?.length) {
      entry.modifier_groups = entry.modifier_group_ids.map((modifierGroupId) => {
        const item = modifierGroupsState.get(modifierGroupId);
        const itemModifiers = item?.modifier_ids?.map((modifier) => setupModifier(modifier)) ?? [];

        return {
          ...item,
          modifiers: itemModifiers,
        };
      });
    }

    return applyOrderMethodPrices(entry);
  }

  const compilePrice = <T extends ProductInterface | Modifier>(product: T, isPromotable: boolean): T => {
    if (isPromotable) {
      return product;
    }

    return {
      ...product,
      actual_price: product.original_price,
      modifier_groups: product.modifier_groups?.map((item) => ({
        ...item,
        modifiers: item.modifiers?.map((modifier) => ({
          ...modifier,
          actual_price: modifier.original_price,
        })),
      })),
    };
  };

  const compileProduct = useCallback(
    (id: number, isPromotable = true): ProductInterface => {
      const products = company?.products ?? {};
      const data = products[id] ?? null;

      if (!data) {
        return null;
      }

      return compilePrice(
        applyOrderMethodPrices({
          ...data,
          modifier_groups: collect(data?.modifier_group_ids)
            .map((id: number) => modifierGroups.get(id))
            .whereNotNull()
            .toArray(),
        }),
        isPromotable,
      );
    },
    [company?.products, modifierGroups, cached_order?.method],
  );

  const mappedCategories = useMemo<Collection<Collection<CategoryInterface>>>(
    () =>
      collect(company?.menus).mapWithKeys((menu) => [
        menu.id,
        collect(menu.category_ids)
          .map((item: number) => {
            const category = company.categories[item];

            return category
              ? {
                  ...category,
                  menu_id: menu.id,
                  products:
                    category.product_ids?.map((id: number) => applyOrderMethodPrices(company.products[id])) ?? [],
                }
              : null;
          })
          .whereNotNull()
          .filter((item) => item.products.length > 0),
      ]),
    [company?.menus, company?.categories, company?.products, cached_order?.method],
  );

  const upsellModifierGroup = useMemo(
    () => modifierGroups.first((modifierGroup) => modifierGroup.type === ModifierGroupType.SystemUpsell),
    [modifierGroups],
  );
  const modifierGroupsToFlatModifiers = (
    modifierGroups: ModifierGroupInterface[],
    result: (Modifier | ProductInterface)[] = [],
  ): (Modifier | ProductInterface)[] => {
    if (!modifierGroups?.length) {
      return result;
    }

    const nextData: ModifierGroupInterface[] = [];

    modifierGroups.forEach((modifierGroup) => {
      if (!modifierGroup.modifier_ids?.length) {
        return;
      }

      result.push(...modifierGroup.modifiers);

      modifierGroup.modifiers.forEach((modifier) => {
        if (!modifier.modifier_group_ids?.length) {
          return;
        }

        nextData.push(...modifier.modifier_groups);
      });
    });

    return modifierGroupsToFlatModifiers(nextData, result);
  };

  const getUpsellModifierGroup = useCallback(
    (addedItems: CompleteBasketItem[], openedProduct: ProductInterface): ModifierGroupInterface => {
      if (!upsellModifierGroup?.modifiers?.length) {
        return;
      }

      const flatAddedItems = addedItems?.length ? collect(toFlatArray(addedItems, 'modifiers')) : collect();

      const openedProducts = collect(modifierGroupsToFlatModifiers(openedProduct?.modifier_groups)).where(
        'type',
        EntryType.Product,
      );
      const openedProductCategoryIds = openedProducts
        .pluck('category_ids')
        .collapse()
        .when(!!openedProduct?.category_ids?.length, (collection) => collection.concat(openedProduct?.category_ids));
      const openedProductNames = openedProducts
        .pluck('name')
        .when(!!openedProduct, (collection) => collection.push(openedProduct.name));

      const addedProducts = flatAddedItems.where('entry.type', EntryType.Product);
      const avoidedProductCategoryIds = addedProducts
        .pluck('entry.category_ids')
        .collapse()
        .concat(openedProductCategoryIds)
        .unique()
        .toArray();
      const avoidedProductNames = addedProducts.pluck('entry.name').concat(openedProductNames).unique().toArray();
      const productsByMenu = mappedCategories.get(selectedMenu?.id)?.pluck('product_ids').collapse().toArray();

      const modifiers = upsellModifierGroup.modifiers
        .filter((modifier) => !avoidedProductNames.includes(modifier.name))
        .filter((modifier) => productsByMenu.includes(modifier.id))
        .filter(
          (modifier: ProductInterface) =>
            !modifier.category_ids.some((categoryId) => avoidedProductCategoryIds.includes(categoryId)),
        )
        .filter((modifier) => modifier.is_available);

      if (modifiers.length > 2) {
        modifiers.length = 2;
      }

      return {
        ...upsellModifierGroup,
        modifiers,
      };
    },
    [modifierGroups, selectedMenu?.id, mappedCategories],
  );

  const menus = useMemo(() => {
    const menus = collect(company?.menus).filter(
      (item) =>
        (!company?.has_pre_order ? new MenuResource(item, company).isActiveToday() : true) &&
        mappedCategories.get(item?.id)?.isNotEmpty(),
    );

    return menus.isEmpty() ? menus.push(collect(company?.menus).first()) : menus;
  }, [company?.menus, company?.has_pre_order, company?.work_schedule, mappedCategories]);

  const bogoItems = useMemo<Collection<ProductInterface>>(
    () =>
      collect(company?.bogo_products).map((item: ProductInterface) => {
        const data = compileProduct(item.id) as ProductInterface;
        return { ...data };
      }),
    [company?.bogo_products],
  );

  const freeItems = useMemo<Collection<ProductInterface>>(
    () =>
      collect(company?.products)
        .where('free_eligible', true)
        .map((item: ProductInterface) => {
          const data = compileProduct(item.id);
          const categories = collect(data.category_ids).filter((item) => {
            const category = company?.categories[item];

            if (!category) {
              return false;
            }

            return collect(company?.menus).whereIn('id', category.menu_ids).isNotEmpty();
          });

          return categories.isNotEmpty()
            ? {
                ...data,
                actual_price: 0,
                discount: 100,
                modifier_groups: collect(data.modifier_groups)
                  .whereNotNull()
                  .map((modifierGroup: ModifierGroupInterface) => ({
                    ...modifierGroup,
                    modifiers: collect(modifierGroup.modifiers)
                      .whereNotNull()
                      .map((modifier: Modifier) => ({
                        ...modifier,
                        actual_price: modifier.free_eligible ? 0 : modifier.actual_price,
                      }))
                      .toArray(),
                  })),
              }
            : null;
        })
        .whereNotNull()
        .keyBy('id'),
    [company?.products, company?.categories, company?.menus, modifierGroups],
  );

  const categories = useMemo<CategoryInterface[]>(() => {
    const freeItemsCategory: CategoryInterface = {
      id: 0,
      menu_id: null,
      menu_ids: [],
      type: CategoryType.FreeItems,
      name: t('labels.free_items'),
      image_url: null,
      product_ids: [],
      products: freeItems.toArray() as ProductInterface[],
    };

    const bogoCategory: CategoryInterface = {
      id: -1,
      menu_id: null,
      menu_ids: [],
      type: CategoryType.BogoItems,
      name: t('labels.bogo'),
      image_url: null,
      product_ids: [],
      products: bogoItems.toArray() as ProductInterface[],
    };

    return [
      ...(bogoItems.count() > 0 ? [bogoCategory] : []),
      ...(hasFreeItem && isLoyaltyProgramActive ? [freeItemsCategory] : []),
      ...((mappedCategories.get(selectedMenu?.id)?.toArray() as CategoryInterface[]) ?? []),
    ];
  }, [hasFreeItem, isLoyaltyProgramActive, freeItems, selectedMenu, mappedCategories, i18n.language]);

  const findFreeProduct = useCallback<(id: number) => ProductInterface>(
    (id: number): ProductInterface => {
      return freeItems.get(id) ?? compileProduct(id);
    },
    [freeItems],
  );

  const isBogoProduct = useCallback(
    (searchProductId: number): boolean => {
      return bogoItems.has(searchProductId);
    },
    [bogoItems],
  );

  useEffect(() => {
    if (!selectedMenu) {
      setSelectedMenu(menus.firstWhere('is_active') ?? menus.first());
    }
  }, [menus]);

  useEffect(() => {
    if (!menus.firstWhere('id', selectedMenu?.id)) {
      setSelectedMenu(menus.firstWhere('is_active') ?? menus.first());
    }
  }, [menus]);

  useEffect(() => {
    if (selectedMenu) {
      preloadImages(
        collect(categories)
          .flatMap((category) => category?.products)
          .pluck('thumbnail_url')
          .filter((url: null | string) => !!url)
          .toArray(),
      );
    }
  }, [selectedMenu]);

  const context: MenuContextInterface = {
    menus,
    selectedMenu,
    setSelectedMenu,
    categories,
    compileProduct,
    findFreeProduct,
    applyOrderMethodPrices,
    isBogoProduct,
    getUpsellModifierGroup,
  };

  return <MenuContext.Provider value={context}>{children}</MenuContext.Provider>;
}
